<template>
  <div class="chart">
    <div class="chart__area">
      <div
        id="chart"
        class="chart__render"
      />

      <div
        v-if="chartType === 'pie'"
        class="chart__timeline"
      >
        <UtilitySlider
          class="chart__timeline--inner"
          variant="timeline"
          :items="timelineItems"
          @toggledItem="toggleTimeline"
        />
      </div>

      <div
        v-if="isLicensed === false"
        class="chart__overlay"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M14 9v2h-4v-2c0-1.104.897-2 2-2s2 .896 2 2zm10 3c0 6.627-5.373 12-12 12s-12-5.373-12-12 5.373-12 12-12 12 5.373 12 12zm-8-1h-1v-2c0-1.656-1.343-3-3-3s-3 1.344-3 3v2h-1v6h8v-6z"/></svg>
      </div>
    </div>

    <div
      v-if="Object.keys(results && results.byId[selectedHash] && results.byId[selectedHash].attributes.data_series ? results.byId[selectedHash].attributes.data_series : {}).length > 1"
      class="chart__legend"
    >
      <ChartLegend
        :unselectedLabels="unselectedLabels"
        @labelToggled="toggleLabel"
        @labelHighlighted="highlightLabel"
        @labelDownplayed="downplayLabel"
      />
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart, PieChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, GraphicComponent, LegendComponent, DataZoomComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import ChartLegend from '@/components/chart/Legend.vue'
import UtilitySlider from '@/components/utility/Slider.vue'
import theme from '@/components/chart/theme.json'
import helperMethods from '@/helpers/utility.js'
echarts.use([TitleComponent, TooltipComponent, GridComponent, DatasetComponent, TransformComponent, GraphicComponent, LegendComponent, DataZoomComponent, BarChart, LineChart, PieChart, CanvasRenderer])

export default {
  components: {
    ChartLegend,
    UtilitySlider
  },
  data() {
    return {
      chart: {},
      //chartType: '',
      chartOptions: {},
      formattedData: {},
      unselectedLabels: [],
      themeJson: theme
    }
  },
  mounted() {
    if (this.results && this.results.byId[this.selectedHash]) {
      this.$store.dispatch('search/setSelectedChartTimeline', '')
      let defaultChart = this.getDefaultChart(this.results.byId[this.selectedHash].attributes.data_series)
      this.chartType = (defaultChart.stack !== false ? 'stacked_' : '') + defaultChart.fullType
      this.$store.dispatch('app/setSettings', { 'name': 'dataset.display', 'value': this.chartType })
      this.$store.dispatch('app/setSettings', { 'name': 'dataset.full_display', 'value': defaultChart })
      //this.$store.dispatch('search/toggleDatasetControl', this.chartType)
      this.formatData()
      this.init()

      window.addEventListener('resize', e => {
        let that = this

        setTimeout(function () {
          that.chart.resize()
        }, 100)
      })
    }
  },
  unmounted() {
    window.removeEventListener('resize')
  },
  watch: {
    chartType: function (value) {
      let parts = value.split('_')
      let fullDisplay = { axis: value.includes('ver') ? 'yAxis' : 'xAxis', fullType: value, stack: value.includes('stacked') ? 'total' : false, type: value === 'pie' ? 'pie' : (parts[2] ? parts[2] : parts[1]) }
      this.$store.dispatch('app/setSettings', { 'name': 'dataset.full_display', 'value': fullDisplay })
      this.formatData()
      this.init()
    }
  },
  computed: {
    chartType: {
      get () {
        return this.$store.getters['app/getSettings'].dataset.display
      },
      set (value) {
        this.$store.dispatch('app/setSettings', { 'name': 'dataset.display', 'value': value })
      }
    },
    fullChartType () {
      return this.$store.getters['app/getSettings'].dataset.full_display
    },
    isLicensed () {
      if (this.results && this.results.byId[this.selectedHash]) {
        return this.results.byId[this.selectedHash].attributes.licensed
      }

      return false
    },
    isDownloadingImage () {
      return this.$store.getters['app/isDownloadingImage']
    },
    selectedDatasetControls () {
      return this.$store.getters['search/getSelectedDatasetControls']
    },
    results () {
      return this.$store.getters['search/getResults'].dataset
    },
    selectedChartTimeline () {
      return this.$store.getters['search/getSelectedChartTimeline']
    },
    selectedHash () {
      return this.$store.getters['search/getSelectedHash']
    },
    title () {
      if (this.results && this.results.byId[this.selectedHash]) {
        return this.results.byId[this.selectedHash].attributes.title
      }

      return false
    },
    scale () {
      if (this.results && this.results.byId[this.selectedHash]) {
        return this.results.byId[this.selectedHash].attributes.scales[0].attributes.name
      }

      return ''
    },
    timelineItems () {
      let items = []
      let rawItems = this.chartOptions.xAxis.data

      if (this.results && this.results.byId[this.selectedHash] && rawItems !== undefined) {
        for (const key of rawItems) {
          // TODO: Not working
          let disabledCheck = this.results.byId[this.selectedHash].attributes.data_series.hasOwnProperty(key) ? false : false // true
          items.push({ 'name': key, 'disabled': disabledCheck })
        }
      }

      return items
    }
  },
  methods: {
    toggleLabel (key) {
      if (this.unselectedLabels.includes(key)) {
        let index = this.unselectedLabels.indexOf(key)
        this.unselectedLabels.splice(index, 1)
        this.chart.dispatchAction({ type: 'legendSelect', name: key })
      } else {
        this.unselectedLabels.push(key)
        this.chart.dispatchAction({ type: 'legendUnSelect', name: key })
      }
    },
    highlightLabel (key) {
      if (this.chartType === 'pie') {
        this.chart.dispatchAction({ type: 'highlight', name: key })
      } else {
        this.chart.dispatchAction({ type: 'highlight', seriesName: key })
      }
    },
    downplayLabel (key) {
      if (this.chartType === 'pie') {
        this.chart.dispatchAction({ type: 'downplay', name: key })
      } else {
        this.chart.dispatchAction({ type: 'downplay', seriesName: key })
      }
    },
    toggleTimeline (key) {
      this.$store.dispatch('search/setSelectedChartTimeline', key.name)
      // THIS WORKS BUT RE-CREATES INSTANCE, NEED TO RE-RENDER ONLY!
      this.formatData()
      this.init()
    },
    filteredData () {
      let filteredData = []
      let rawData = this.results.byId[this.selectedHash].attributes.data_series

      for (const [key, value] of Object.entries(rawData)) {
        filteredData.push({ 'name': key, 'value': rawData[key][this.selectedChartTimeline] })
      }

      return filteredData
    },
    formatData() {
      let _this = this
      let series = {}
      let formattedSeries = []
      let formattedKeys = []

      if (this.results && this.results.byId[this.selectedHash] && this.results.byId[this.selectedHash].attributes.data_series) {
        Object.entries(this.results.byId[this.selectedHash].attributes.data_series).forEach(row => {
          let [key, value] = row

          if (this.fullChartType.type === 'pie') {
            series = {
              name: this.selectedChartTimeline,
              type: 'pie',
              radius: '60%',
              center: ['50%', '47.5%'],
              data: this.filteredData(),
              itemStyle: {
                borderWidth: 2,
                borderColor: 'rgb(27, 30, 33)',
              },
              label: {
                //show: false
              },
              labelLayout: {
                hideOverlap: true
              },
              emphasis: {
                itemStyle: {
                  
                }
              }
            }
          } else {
            series = {
              name: key,
              type: this.fullChartType.type === 'bar' ? 'bar' : 'line',
              data: Object.values(value),
              stack: this.fullChartType.stack,
              barMaxWidth: '75',
              itemStyle: {
                borderWidth: 0
              },
              symbol: 'circle',
              symbolSize: 9,
              lineStyle: {
                width: 4
              },
              emphasis: {
                focus: 'series',
                label: {
                  show: true,
                  position: 'inside',
                  color: 'rgb(255,255,255)',
                  textBorderWidth: 0,
                  textBorderColor: 'rgba(0,0,0,0.5)',
                  textShadowColor: 'rgba(0,0,0,0.7)',
                  textShadowBlur: 3,
                  fontSize: 13,
                  fontWeight: 'bold',
                  formatter: function (value) {
                    return _this.isLicensed ? _this.formatNumber(value.data, 1) : '??'
                  }
                }
              }
            }
          }

          formattedSeries.push(series)
          formattedKeys = formattedKeys.concat(Object.keys(value))
        })

        this.formattedData = {
          series: formattedSeries,
          keys: [... new Set(formattedKeys)]
        }

        if (this.selectedChartTimeline === '') {
          this.$store.dispatch('search/setSelectedChartTimeline', this.formattedData.keys[0])
        }
      }

      return false
    },
    init() {
      let _this = this
      let option = {}

      if (Object.keys(this.chart).length === 0) {
        echarts.registerTheme('statzon', this.themeJson)
        this.chart = echarts.init(document.getElementById('chart'), 'statzon')
      }

      if (this.chartType === 'ver_bar' || this.chartType === 'hor_bar' || this.chartType === 'stacked_ver_bar' || this.chartType === 'stacked_hor_bar' || this.chartType === 'ver_line' || this.chartType === 'hor_line') {
        option = {
          backgroundColor: 'rgb(27,30,33)',
          title: {
            show: true,
            text: this.isDownloadingImage ? this.title : '',
            left: "8",
            top: "0",
            subtext: this.scale,
            textStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 20,
              fontWeight: 'normal'
            },
            subtextStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 16,
              fontWeight: 'normal'
            }
          },
          graphic: {
            elements: [
              {
                type: 'image',
                id: 'logo',
                left: 'center',
                top: '20%',
                z: -1,
                bounding: 'raw',
                style: {
                  image: '/img/statzon_logo_white.svg',
                  width: 400,
                  opacity: 0.01
                }
              }
            ]
          },
          legend: {
            show: false
          },
          tooltip: {
            show: _this.isLicensed ? true : false,
            trigger: 'item',
            confine: true,
            axisPointer: {
              type: 'shadow',
              animation: true,
              shadowStyle: {
                color: 'rgba(255,255,255,0.1)'
              }
            },
            transitionDuration: 0.25,
            backgroundColor: 'rgb(18,19,15)',
            borderWidth: 0,
            padding: 10,
            textStyle: {
              color: 'rgba(255,255,255,0.7)',
              fontSize: 14,
            },
            order: 'seriesDesc'
          },
          grid: {
            left: '16',
            right: '40',
            top: this.isDownloadingImage ? '64' : '48',
            bottom: '40',
            containLabel: true
          },
          dataZoom: [{
            show: true,
            showDetail: false,
            type: 'slider',
            xAxisIndex: [0],
            filterMode: 'filter',
            height: '16',
            left: '8',
            right: '12',
            bottom: '16',
            backgroundColor: 'rgb(27,30,33)',
            dataBackground: {
              lineStyle: {
                color: 'rgba(255,255,255,0.1)',
                width: 2
              },
              areaStyle: {
                color: 'rgb(39,43,47)',
                opacity: 0.5
              }
            },
            selectedDataBackground: {
              lineStyle: {
                color: 'rgba(0,0,0,0.5)',
                width: 2
              },
              areaStyle: {
                color: 'rgba(255,255,255,0.1)',
                opacity: 1
              }
            },
            fillerColor: 'rgba(255,255,255,0.05)',
            handleIcon: 'image://url',
            handleSize: '16',
            handleStyle: {
              color: 'rgba(255,0,0,1)',
              borderColor: 'rgba(0,0,0,1)',
              borderWidth: 1,
              opacity: 0.25
            },
            moveHandleIcon: 'image://url',
            moveHandleSize: 6,
            moveHandleStyle: {
              color: 'rgba(255,255,255,0)',
              opacity: 1
            },
            emphasis: {
              handleIcon: 'path://M6 11v-4l-6 5 6 5v-4h12v4l6-5-6-5v4z',
              handleStyle: {
                borderColor: 'rgb(21,147,166)',
                opacity: 1
              },
              moveHandleStyle: {
                color: 'rgba(255,255,255,0.25)',
              },
            }
          }, {
            show: true,
            showDetail: false,
            type: 'slider',
            yAxisIndex: [0],
            filterMode: 'filter',
            width: '16',
            right: '10',
            top: '16',
            bottom: '46',
            backgroundColor: 'rgb(27,30,33)',
            dataBackground: {
              lineStyle: {
                color: 'rgba(255,255,255,0.1)',
                width: 2
              },
              areaStyle: {
                color: 'rgb(39,43,47)',
                opacity: 0.5
              }
            },
            selectedDataBackground: {
              lineStyle: {
                color: 'rgba(0,0,0,0.5)',
                width: 2
              },
              areaStyle: {
                color: 'rgba(255,255,255,0.1)',
                opacity: 1
              }
            },
            fillerColor: 'rgba(255,255,255,0.05)',
            handleIcon: 'image://url',
            handleSize: '16',
            handleStyle: {
              color: 'rgba(255,0,0,1)',
              borderColor: 'rgba(0,0,0,1)',
              borderWidth: 1,
              opacity: 0.25
            },
            moveHandleIcon: 'image://url',
            moveHandleSize: 6,
            moveHandleStyle: {
              color: 'rgba(255,255,255,0)',
              opacity: 1
            },
            emphasis: {
              handleStyle: {
                borderColor: 'rgb(21,147,166)',
                opacity: 1
              },
              moveHandleStyle: {
                color: 'rgba(255,255,255,0.25)',
              },
            }
          }],
          yAxis: this.chartType === 'ver_bar' || this.chartType === 'stacked_ver_bar' || this.chartType === 'hor_line' ? {
            show: true,
            type: 'value',
            axisLabel: {
              fontSize: 14,
              formatter: function (value, index) {
                return _this.isLicensed ? _this.formatNumber(value, 1) : '??'
              }
            }
          } : {
            show: true,
            type: 'category',
            data: this.formattedData.keys,
            axisTick: {
              show: false
            },
            axisLabel: {
              fontSize: 14,
              formatter: function (value, index) {
                return value.length > 40 ? value.slice(0, 37) + '...' : value
              }
            }
          },
          xAxis: this.chartType === 'hor_bar' || this.chartType === 'stacked_hor_bar' || this.chartType === 'ver_line' ? {
            show: true,
            type: 'value',
            axisLabel: {
              fontSize: 14,
              formatter: function (value, index) {
                return _this.isLicensed ? _this.formatNumber(value, 1) : '??'
              }
            }
          } : {
            show: true,
            type: 'category',
            data: this.formattedData.keys,
            axisTick: {
              show: false
            },
            axisLabel: {
              fontSize: 14
            }
          },
          series: this.formattedData.series
        }
      }

      else if (this.chartType === 'pie') {
        option = {
          backgroundColor: 'rgb(27,30,33)',
          title: {
            show: true,
            text: '',
            left: "8",
            top: "0",
            subtext: this.scale,
            textStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 20,
              fontWeight: 'normal'
            },
            subtextStyle: {
              color: 'rgb(255,255,255)',
              fontSize: 16,
              fontWeight: 'normal'
            }
          },
          graphic: {
            elements: [
              {
                type: 'image',
                id: 'logo',
                left: 'center',
                top: '11%',
                z: -1,
                bounding: 'raw',
                style: {
                  image: '/img/statzon_logo_white.svg',
                  width: 400,
                  opacity: 0.01
                }
              }
            ]
          },
          legend: {
            show: false
          },
          tooltip: {
            trigger: 'item'
          },
          grid: {
            left: '16',
            right: '40',
            top: '48',
            bottom: '40',
            containLabel: true
          },
          dataZoom: [{
            show: false
          },{
            show: false
          }],
          yAxis: {
            show: false,
          },
          xAxis: {
            show: false,
            type: 'category',
            data: this.formattedData.keys
          },
          avoidLabelOverlap: true,
          stillShowZeroSum: false,
          series: this.formattedData.series
        }
      }

      this.chartOptions = option
      this.chart.setOption(this.chartOptions)
    },
    ...helperMethods
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.chart {
  width: 100%;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba($white, 0.1);

  @media screen and (min-width: $breakpointMd) {
    flex-direction: row;
  }

  &__area {
    position: relative;
    flex: 1;

    @media screen and (min-width: $breakpointMd) {
      border-right: 1px solid $black;
    }
  }

  &__render {
    width: 100%;
    height: 460px;

    @media screen and (min-width: $breakpointMd) {
      height: 500px;
    }

    @media screen and (min-width: $breakpointLg) {
      height: 540px;
    }

    @media screen and (min-width: $breakpointXl) {
      height: 580px;
    }
  }

  &__timeline {
    position: absolute;
    bottom: 0px;
    width: 100%;
    height: 36px;
    border-top: 1px solid $black;
    background-color: $bg-2;

    &--inner {
      border-top: 1px solid rgba($white, 0.1);
    }
  }

  &__legend {
    width: 100%;
    //background-color: $bg-2;
    border-top: 1px solid $black;

    @media screen and (min-width: $breakpointMd) {
      width: 277px;
      border-top: none;
      //border-left: 1px solid $black;
      //border-right: 1px solid $black;
    }

    @media screen and (min-width: $breakpointLg) {
      width: 299px;
    }

    @media screen and (min-width: $breakpointXl) {
      width: 360px;
    }
  }

  &__overlay {
    position: absolute;
    width: 180px;
    height: 180px;
    top: calc(47.5% - 106px);
    left: calc(50% - 106px);
    background-color: rgba($bg-2, 0.9);
    border-radius: 9999px;
    border: 16px solid $bg-2;
    pointer-events: none;

    svg {
      position: relative;
      width: 100%;
      height: 100%;
      fill: rgba($white, 0.5);
    }

    @media screen and (min-width: $breakpointMd) {
      width: 200px;
      height: 200px;
      top: calc(47.5% - 116px);
      left: calc(50% - 116px);
    }

    @media screen and (min-width: $breakpointLg) {
      width: 220px;
      height: 220px;
      top: calc(47.5% - 126px);
      left: calc(50% - 126px);
    }
  }
}

</style>